import { Component, OnInit } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { LoaderService } from '../loader.service';
import { AlertDialogService } from '../shared/alert-dialog/alert-dialog.service';
import { SharedService } from '../shared/shared.service';
import { ToasterService } from '../toaster.service';
import { Toaster, ToasterType } from '../shared/types/toaster.types';
import { SubscriptionService } from './subscription.service';
import { PostpaidContext } from './postpaid/create/postpaid.create.component';
import { PrepaidContext } from './prepaid/create/prepaid.create.component';
import { AccessFeeContext } from './accessFee/create/accessFee.create.component';
import { planTypeFromRule, servicesForExclusion, skipServiceHierarchyForMode, eligibleRuleTypesFromRule, displayTitlesFromRule, exclusiveOfTaxFromRule, applicableConfig } from './subscription.applicable-types';
import { AuthzAccessHintService } from '../shared/authz-access-hint/authz-access-hint.service';

@Component({
selector: 'app-subscription',
templateUrl: './subscription.component.html',
styleUrls: ['./subscription.component.css']
})
export class SubscriptionComponent implements OnInit {

    public subscriptionType: string = 'accessFee';
    public authzAccessHint: { message: string; requestUrl: string } | null = null;
    public launchParams: { city: string, service: string } = null;
    private hasSwitchedTab = false;
    private launchResolved = false;

    public configFormGroup = new FormGroup({
      creationFlow: new FormControl('earningModel'),
      state: new FormControl(null),
      city: new FormControl(null),
      mode: new FormControl(null),
      category: new FormControl(null),
      service: new FormControl(null)
    });

    public states: { id: string, name: string, cities: { id: string, name: string }[] }[] = [];
    public cities: { id: string, name: string }[] = [];
    public modes: { id: string, name: string }[] = [];
    public categories: { id: string, name: string }[] = [];
    public services: { id: string, name: string, serviceDetailId: string, categoryId: string, active: boolean }[] = [];
    public applicablePlans: string[] = [];
    public matchedRule: any = null;
    public trialEnabledForSelectedCategory = false;
    private allServices: { id: string, name: string, serviceDetailId: string, categoryId: string, active: boolean }[] = [];
    private configsBySdid: { [sdid: string]: any } = {};
    private applicableFetchId = 0;
    private trialCategoryFetchId = 0;
    private skipServiceHierarchyToggle: any = {};

    constructor(
      public toasterService: ToasterService,
      private route: ActivatedRoute,
      private router: Router,
      private sharedService: SharedService,
      private subsService: SubscriptionService,
      private loader: LoaderService,
      private alertService: AlertDialogService,
      private authzAccessHintService: AuthzAccessHintService,
    ) { }

    ngOnInit() {
      this.loadAccessHint();
      const q = (this.route.snapshot && this.route.snapshot.queryParams) || {};
      const city = (q['city'] || '').trim();
      if (city) {
        this.launchParams = { city, service: (q['service'] || '').trim() };
      }

      this.configFormGroup.get('creationFlow').valueChanges.subscribe(() => this.onCreationFlowChange());
      this.configFormGroup.get('state').valueChanges.subscribe(() => this.onStateChange());
      this.configFormGroup.get('city').valueChanges.subscribe(() => this.onCityChange());
      this.configFormGroup.get('mode').valueChanges.subscribe(() => this.onModeChange());
      this.configFormGroup.get('category').valueChanges.subscribe(() => this.onCategoryChange());
      this.configFormGroup.get('service').valueChanges.subscribe(() => this.onServiceChange());

      this.loadInitialData();
    }

    private async loadAccessHint() {
      this.authzAccessHint = await this.authzAccessHintService.getAccessHintData('subscription');
    }

    get postpaidContext(): PostpaidContext {
      const state = this.configFormGroup.get('state').value;
      const city = this.configFormGroup.get('city').value;
      const mode = this.configFormGroup.get('mode').value;
      const category = this.configFormGroup.get('category').value;
      const service = this.configFormGroup.get('service').value;
      if (!state || !city || !mode || !category || !service) {
        return null;
      }
      return {
        state,
        city,
        mode,
        category: category.id,
        categoryServices: this.services,
        excludableServices: servicesForExclusion(this.configsBySdid, this.services),
        displayTitles: displayTitlesFromRule(this.matchedRule),
        exclusiveOfTax: exclusiveOfTaxFromRule(this.matchedRule),
        eligibleRuleTypes: eligibleRuleTypesFromRule(this.matchedRule, ['zeroCommission'])
      };
    }

    get prepaidContext(): PrepaidContext {
      const city = this.configFormGroup.get('city').value;
      const mode = this.configFormGroup.get('mode').value;
      const category = this.configFormGroup.get('category').value;
      const service = this.configFormGroup.get('service').value;
      if (!city || !mode || !category || !service) {
        return null;
      }
      const applicable = applicableConfig(this.matchedRule) || {};
      return {
        city,
        mode,
        service,
        modeServices: this.allServices,
        categoryServices: this.services,
        skipServiceHierarchy: skipServiceHierarchyForMode(this.skipServiceHierarchyToggle, mode.id, city.id),
        autoAssignmentTriggers: applicable.autoAssignmentTriggers || [],
        allowSuspendServicesPostExpiry: applicable.allowSuspendServicesPostExpiry === true,
        alwaysSuspendServicesPostExpiry: applicable.alwaysSuspendServicesPostExpiry === true,
        allowDefaultSubscription: applicable.allowDefaultSubscription === true,
        eligibleRuleTypes: eligibleRuleTypesFromRule(this.matchedRule, ['zeroCommission']),
        displayTitles: displayTitlesFromRule(this.matchedRule),
        exclusiveOfTax: exclusiveOfTaxFromRule(this.matchedRule)
      };
    }

    get daprContext() {
      const city = this.configFormGroup.get('city').value;
      const mode = this.configFormGroup.get('mode').value;
      if (!city || !mode) {
        return null;
      }
      return {
        city,
        mode,
        modeServices: this.allServices,
        exclusiveOfTax: false
      };
    }

    get trialContext() {
      const city = this.configFormGroup.get('city').value;
      const mode = this.configFormGroup.get('mode').value;
      const category = this.configFormGroup.get('category').value;
      const service = this.configFormGroup.get('service').value;
      if (!city || !mode || !category || !service) {
        return null;
      }
      const categoryServices = this.allServices.filter(
        serviceItem => String(serviceItem.categoryId) === String(category.id)
      );
      return {
        city,
        mode,
        category,
        service,
        modeServices: categoryServices
      };
    }

    get isDaprResetMe() {
      return this.configFormGroup.get('creationFlow').value === 'daprResetMe';
    }

    get isTrialSubs() {
      return this.configFormGroup.get('creationFlow').value === 'trialSubs';
    }

    get isStandaloneCreationFlow() {
      return this.isDaprResetMe || this.isTrialSubs;
    }

    get showParentService() {
      return !this.isDaprResetMe && !this.isTrialSubs && !this.isPlanLoaded;
    }

    get showAccessFee() {
      return !this.isStandaloneCreationFlow && this.subscriptionType === 'accessFee' && this.isPlanApplicable('accessFee');
    }

    get showEarningPrepaid() {
      return !this.isStandaloneCreationFlow && this.subscriptionType === 'subscription' && this.isPlanApplicable('subscription') && !!this.prepaidContext;
    }

    get showDaprPrepaid() {
      return this.isDaprResetMe && !!this.daprContext;
    }

    get showTrialSubs() {
      return this.isTrialSubs && this.trialEnabledForSelectedCategory && !!this.trialContext;
    }

    get showPostpaid() {
      return !this.isStandaloneCreationFlow && this.subscriptionType === 'postpaid' && this.isPlanApplicable('postpaid');
    }

    get isPlanLoaded() {
      return this.applicablePlans.length > 0;
    }

    get accessFeeContext(): AccessFeeContext {
      const city = this.configFormGroup.get('city').value;
      const mode = this.configFormGroup.get('mode').value;
      const category = this.configFormGroup.get('category').value;
      const service = this.configFormGroup.get('service').value;
      if (!city || !mode || !service) {
        return null;
      }
      return {
        city,
        mode,
        category,
        service,
      };
    }

    get accessFeeLaunch() {
      const city = this.configFormGroup.get('city').value;
      const service = this.configFormGroup.get('service').value;
      if (city && service) {
        return { city: city.id, service: service.id };
      }
      return this.launchParams;
    }

    isPlanApplicable(plan: string) {
      return this.applicablePlans.indexOf(plan) !== -1;
    }

    onLaunchConsumed() {
      this.launchResolved = true;
      this.launchParams = null;
    }

    onManualTabChange() {
      this.onLaunchConsumed();
    }

    onLaunchServiceUnavailable() {
      if (this.launchResolved) {
        return;
      }
      if (!this.hasSwitchedTab) {
        this.hasSwitchedTab = true;
        this.subscriptionType = this.subscriptionType === 'accessFee' ? 'subscription' : 'accessFee';
        return;
      }
      this.onLaunchConsumed();
      this.toasterService.showToaster(new Toaster({
        type: ToasterType.WARNING,
        message: 'Service from URL is not available for subscription creation.'
      }));
    }

    private loadInitialData() {
      this.loader.openLoading();
      Promise.all([
        this.sharedService.fetchStatesAndCities().toPromise(),
        this.subsService.getSkipServiceHierarchyToggle().toPromise().catch(() => ({ data: {} }))
      ]).then(([statesRes, skipRes]: any[]) => {
        this.states = (statesRes && statesRes.data && statesRes.data.states) || [];
        this.skipServiceHierarchyToggle = (skipRes && skipRes.data) || skipRes || {};
        return this.applyLaunchPrefill();
      }).catch((err) => {
        console.error('Failed to load states or applicable types', err);
        this.toasterService.showToaster(new Toaster({
          type: ToasterType.WARNING,
          message: 'Could not load states.'
        }));
      }).finally(() => this.loader.closeLoading());
    }

    private findLaunchStateAndCity(cityId: string) {
      let matchedState = null;
      let matchedCity = null;
      (this.states || []).forEach(state => {
        const city = (state.cities || []).find(c => String(c.id) === String(cityId));
        if (city && !matchedCity) {
          matchedState = state;
          matchedCity = city;
        }
      });
      return matchedState ? { state: matchedState, city: matchedCity } : null;
    }

    private unwrapServiceDetail(item) {
      return item && item.data ? item.data : item;
    }

    private categoryFromServiceDetail(data) {
      const service = (data && data.service) || {};
      const candidates = [data && data.category, data && data.serviceCategory, service.category];
      for (let i = 0; i < candidates.length; i++) {
        const raw = candidates[i];
        if (raw && typeof raw === 'object') {
          return {
            id: String(raw._id || raw.id || raw.name),
            name: String(raw.name || raw.displayName || raw._id || raw.id)
          };
        }
        if (typeof raw === 'string' && raw) {
          return { id: raw, name: raw };
        }
      }
      const name = service.categoryName || (data && data.categoryName);
      return name ? { id: String(name), name: String(name) } : null;
    }

    private matchServiceDetail(details, cityId: string, serviceId: string) {
      const rows = (details || []).map(item => this.unwrapServiceDetail(item)).filter(data => {
        if (!data) {
          return false;
        }
        const itemCity = data.city && (data.city._id || data.city.id);
        if (String(itemCity) !== String(cityId)) {
          return false;
        }
        const service = data.service || {};
        const svcId = service._id || service.id;
        return String(svcId) === String(serviceId) || String(data._id) === String(serviceId);
      });
      return rows.find(row => row.active !== false) || rows[0] || null;
    }

    private async applyLaunchPrefill() {
      const cityId = this.launchParams && this.launchParams.city;
      const serviceId = this.launchParams && this.launchParams.service;
      if (!cityId) {
        return;
      }
      const found = this.findLaunchStateAndCity(cityId);
      if (!found) {
        this.toasterService.showToaster(new Toaster({
          type: ToasterType.WARNING,
          message: 'City from URL is not available.'
        }));
        return;
      }
      this.configFormGroup.get('state').setValue(found.state, { emitEvent: false });
      this.cities = found.state.cities || [];
      this.configFormGroup.get('city').setValue(found.city, { emitEvent: false });
      if (!serviceId) {
        this.onCityChange();
        return;
      }
      try {
        const detailsRes: any = await this.sharedService.fetchAllServiceDetails().toPromise();
        const row = this.matchServiceDetail((detailsRes && detailsRes.data) || detailsRes || [], cityId, serviceId);
        if (!row) {
          this.toasterService.showToaster(new Toaster({
            type: ToasterType.WARNING,
            message: 'Service from URL is not available.'
          }));
          this.onCityChange();
          return;
        }
        const modeId = row.modeInfo && (row.modeInfo._id || row.modeInfo.id);
        const modeName = row.modeInfo && (row.modeInfo.mode || row.modeInfo.name);
        const categoryHint = this.categoryFromServiceDetail(row);
        if (!modeId || !categoryHint) {
          this.toasterService.showToaster(new Toaster({
            type: ToasterType.WARNING,
            message: 'Could not resolve mode and category from service.'
          }));
          this.onCityChange();
          return;
        }
        const modesRes: any = await this.sharedService.fetchModes(cityId).toPromise();
        this.modes = (modesRes && modesRes.data && modesRes.data.modes) || [];
        const mode = this.modes.find(item => String(item.id) === String(modeId))
          || { id: String(modeId), name: modeName || String(modeId) };
        if (!this.modes.some(item => String(item.id) === String(mode.id))) {
          this.modes = this.modes.concat([mode]);
        }
        this.configFormGroup.get('mode').setValue(mode, { emitEvent: false });

        const optionsRes: any = await this.sharedService.fetchSubscriptionServiceOptions(cityId, mode.id).toPromise();
        this.categories = (optionsRes && optionsRes.data && optionsRes.data.categories) || [];
        this.allServices = (optionsRes && optionsRes.data && optionsRes.data.services) || [];
        const category = this.categories.find(item => String(item.id) === String(categoryHint.id)) || categoryHint;
        if (!this.categories.some(item => String(item.id) === String(category.id))) {
          this.categories = this.categories.concat([category]);
        }
        this.configFormGroup.get('category').setValue(category, { emitEvent: false });
        this.services = this.allServices.filter(item => String(item.categoryId) === String(category.id));
        const service = this.services.find(item =>
          String(item.id) === String(serviceId) || String(item.serviceDetailId) === String(serviceId)
        );
        if (!service) {
          this.toasterService.showToaster(new Toaster({
            type: ToasterType.WARNING,
            message: 'Service from URL is not available.'
          }));
          return;
        }
        this.configFormGroup.get('service').setValue(service);
      } catch (err) {
        console.error('Launch prefill failed', err);
        this.toasterService.showToaster(new Toaster({
          type: ToasterType.WARNING,
          message: 'Could not prefill city and service from URL.'
        }));
        this.onCityChange();
      }
    }

    private resetDownstream(controls: string[]) {
      this.applicableFetchId++;
      controls.forEach(name => this.configFormGroup.get(name).setValue(null, { emitEvent: false }));
      this.applicablePlans = [];
      this.matchedRule = null;
      this.configsBySdid = {};
      if (this.isTrialSubs && (controls.indexOf('mode') >= 0 || controls.indexOf('category') >= 0)) {
        this.trialEnabledForSelectedCategory = false;
      }
    }

    private invalidateTrialSelection() {
      if (!this.isTrialSubs) {
        return;
      }
      this.trialCategoryFetchId++;
      this.trialEnabledForSelectedCategory = false;
    }

    private syncSelectionInUrl() {
      const city = this.configFormGroup.get('city').value;
      const service = this.configFormGroup.get('service').value;
      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: {
          city: city && city.id ? city.id : null,
          service: service && service.id ? service.id : null
        },
        queryParamsHandling: 'merge',
        replaceUrl: true
      });
    }

    private onCreationFlowChange() {
      this.trialEnabledForSelectedCategory = false;
      this.invalidateTrialSelection();
      this.onServiceChange();
    }

    private onStateChange() {
      const state = this.configFormGroup.get('state').value;
      this.cities = state && state.cities ? state.cities : [];
      this.modes = [];
      this.categories = [];
      this.services = [];
      this.allServices = [];
      this.resetDownstream(['city', 'mode', 'category', 'service']);
      this.syncSelectionInUrl();
    }

    private onCityChange() {
      const city = this.configFormGroup.get('city').value;
      this.invalidateTrialSelection();
      this.modes = [];
      this.categories = [];
      this.services = [];
      this.allServices = [];
      this.resetDownstream(['mode', 'category', 'service']);
      this.syncSelectionInUrl();
      if (!city) {
        return;
      }
      this.loader.openLoading();
      this.sharedService.fetchModes(city.id).toPromise()
        .then((res: any) => {
          this.modes = (res && res.data && res.data.modes) || [];
        })
        .catch((err) => {
          console.error('Failed to load modes', err);
          this.toasterService.showToaster(new Toaster({
            type: ToasterType.WARNING,
            message: 'Could not load modes for city.'
          }));
        })
        .finally(() => this.loader.closeLoading());
    }

    private onModeChange() {
      const city = this.configFormGroup.get('city').value;
      const mode = this.configFormGroup.get('mode').value;
      this.categories = [];
      this.services = [];
      this.allServices = [];
      this.invalidateTrialSelection();
      this.resetDownstream(['category', 'service']);
      this.syncSelectionInUrl();
      if (!city || !mode) {
        return;
      }
      this.loader.openLoading();
      this.sharedService.fetchSubscriptionServiceOptions(city.id, mode.id).toPromise()
        .then((res: any) => {
          this.categories = (res && res.data && res.data.categories) || [];
          this.allServices = (res && res.data && res.data.services) || [];
        })
        .catch((err) => {
          console.error('Failed to load categories and services', err);
          this.toasterService.showToaster(new Toaster({
            type: ToasterType.WARNING,
            message: 'Could not load categories for mode.'
          }));
        })
        .finally(() => this.loader.closeLoading());
    }

    private onCategoryChange() {
      const category = this.configFormGroup.get('category').value;
      this.invalidateTrialSelection();
      this.services = category
        ? this.allServices.filter(service => String(service.categoryId) === String(category.id))
        : [];
      this.resetDownstream(['service']);
      this.subscriptionType = null;
      this.syncSelectionInUrl();
    }

    private onServiceChange() {
      this.applicableFetchId++;
      const city = this.configFormGroup.get('city').value;
      const mode = this.configFormGroup.get('mode').value;
      const category = this.configFormGroup.get('category').value;
      const service = this.configFormGroup.get('service').value;
      this.applicablePlans = [];
      this.matchedRule = null;
      this.subscriptionType = null;
      this.configsBySdid = {};
      this.syncSelectionInUrl();
      if (!city || !mode) {
        return;
      }
      if (this.isDaprResetMe) {
        this.subscriptionType = 'subscription';
        this.applicablePlans = ['subscription'];
        return;
      }
      if (this.isTrialSubs) {
        if (!category || !service) {
          this.trialEnabledForSelectedCategory = false;
          return;
        }
        this.trialCategoryFetchId++;
        this.trialEnabledForSelectedCategory = false;
        const sdid = service.serviceDetailId;
        const fetchId = this.trialCategoryFetchId;
        this.loader.openLoading();
        this.subsService.getApplicableTypes([sdid], mode.id).toPromise()
          .then((res: any) => {
            if (fetchId !== this.trialCategoryFetchId || !this.isTrialSubs) {
              return;
            }
            const selected = this.configFormGroup.get('service').value;
            if (!selected || String(selected.serviceDetailId) !== String(sdid)) {
              return;
            }
            const config = (res && res.data && res.data[String(sdid)]) || null;
            if (!config || config.eligibleForTrial !== true) {
              this.configFormGroup.get('service').setValue(null, { emitEvent: false });
              this.alertService.open({
                title: 'Trial not enabled',
                message: `Trial is not enabled for mode "${mode.name}", category "${category.name}", service "${service.name}".`
              });
              return;
            }
            this.trialEnabledForSelectedCategory = true;
          })
          .catch((err) => {
            if (fetchId !== this.trialCategoryFetchId) {
              return;
            }
            console.error('Failed to validate trial selection', err);
            this.configFormGroup.get('service').setValue(null, { emitEvent: false });
            this.alertService.open({
              title: 'Trial not enabled',
              message: `Could not load trial config for mode "${mode.name}", category "${category.name}", service "${service.name}".`
            });
          })
          .finally(() => {
            if (fetchId === this.trialCategoryFetchId) {
              this.loader.closeLoading();
            }
          });
        return;
      }
      if (!category || !service) {
        return;
      }
      const sdids = this.services.map(item => item.serviceDetailId).filter(Boolean);
      if (!sdids.length) {
        return;
      }
      const fetchId = this.applicableFetchId;
      const loadingTask = `subscription-applicable-${fetchId}`;
      this.loader.openLoading(loadingTask);
      this.subsService.getApplicableTypes(sdids, mode.id).toPromise()
        .then((res: any) => {
          if (fetchId !== this.applicableFetchId || this.isStandaloneCreationFlow) {
            return;
          }
          this.configsBySdid = (res && res.data) || {};
          const config = this.configsBySdid[service.serviceDetailId] || null;
          this.matchedRule = config ? { applicableType: config } : null;
          const planType = planTypeFromRule(this.matchedRule);
          this.subscriptionType = planType;
          this.applicablePlans = planType ? [planType] : [];
          if (!planType) {
            this.alertService.open({
              title: 'Not eligible',
              message: 'This service is not eligible for any type of subscription. If that\'s a mistake, contact @captain-retention-be-devs in the #captain-retention-team Slack channel'
            });
          }
        })
        .catch((err) => {
          if (fetchId !== this.applicableFetchId || this.isStandaloneCreationFlow) { return; }
          console.error('Failed to load applicable type', err);
          this.toasterService.showToaster(new Toaster({
            type: ToasterType.WARNING,
            message: 'Could not load applicable type for service.'
          }));
        })
        .finally(() => {
          this.loader.closeLoading(loadingTask);
        });
    }

}
